import { CreateViewStateFn, ExportFormat } from '@ecosystem/newron-client-lib';
import React from 'react';
import {
  dataExportInitiatedTrackingFunction,
  DataExportTrackingElementKpiAndSortParam,
  useDataExportTracking,
} from '@utils/page-analytics/model/DataExportTracking';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { useExportModal } from '@hooks/export/export-modal/UseExportModal';
import { EnabledExportPages, ViewType } from '@hooks/export/Model';
import { CreateInsightDataFn } from '@ecosystem/newron-client-collections';
import { CategoryLeaderboardDoubleBarChartInsight } from '@components/collections/category-leaderboard-double-bar-chart/Model';
import { PerformanceAnalysisLineChartInsight } from '@components/collections/performance-analysis-line-chart/Model';
import ExploreCollectionButton from '@pages/explore/collection-button/ExploreCollectionButton';
import { ExploreTabView } from '@components/tab-nav/TabConfig';
import { WithExportButton, WithExportModal } from '@components/design';
import { ToggleViewButtons } from '@components/toggle-view-buttons/ToggleViewButton';
import { getDisplayNameFromHeaderKey } from '@models/PerformanceAnalysisboard';
import { PerformanceAnalysisStackedBarChartInsight } from '@components/collections/performance-analysis-stacked-bar-chart/Model';
import {
  ExploreView,
  ExploreViewElementTypeMapping,
  ExploreViewViewTypeMapping,
} from '@utils/exploreUtils/ExploreType';
import { TrendStackBarChartInsight } from '@components/collections/explore-trends-stacked-bar-chart/Model';

export interface MarketCardHeaderIcon {
  icon: any;
  title: string;
  onClick: () => void;
  isActive: boolean;
}

interface MarketCardHeaderIconProps {
  onChartDownload: (exportFormats: ExportFormat[]) => Promise<void>;
  isExportButtonDisabled: boolean;
  isStackChartExportButtonDisabled?: boolean;
  volume: string;
  createInsightDataFn: CreateInsightDataFn<
    | CategoryLeaderboardDoubleBarChartInsight
    | PerformanceAnalysisLineChartInsight
    | PerformanceAnalysisStackedBarChartInsight
    | TrendStackBarChartInsight
  >;
  exploreChartView: ExploreView;
  exploreTabView: ExploreTabView;
  onChange?: (view: ViewType) => void;
  createViewStateFn?: CreateViewStateFn<any>;
}

const ExploreHeaderIcon: React.FC<MarketCardHeaderIconProps> = ({
  onChartDownload,
  isExportButtonDisabled,
  isStackChartExportButtonDisabled,
  volume,
  createInsightDataFn,
  exploreChartView,
  exploreTabView,
  onChange,
  createViewStateFn,
}) => {
  const pagesConfig = usePageConfig();

  const viewType = ExploreViewViewTypeMapping[exploreChartView];
  const elementType = ExploreViewElementTypeMapping[exploreChartView];

  const {
    openExportModal,
    onCloseExportModal,
    onExportError,
    exportModalFormats,
    exportModalMessage,
    exportModalHeaderText,
    isExportModalOpen,
  } = useExportModal({
    currentPage: EnabledExportPages.Explore,
    currentTab: exploreTabView,
    currentView: viewType,
  });

  const handleDataExportTrackingFunction = useDataExportTracking('mainFilters');

  const getElementKpiAndSortParam = (): DataExportTrackingElementKpiAndSortParam => {
    return {
      elementKpi: getDisplayNameFromHeaderKey(pagesConfig.sortingOptions.headerKey, volume),
      elementSort: pagesConfig.sortingOptions,
    };
  };

  const handleExportTracking = () => {
    handleDataExportTrackingFunction(
      {
        elementType: elementType,
        ...getElementKpiAndSortParam(),
      },
      dataExportInitiatedTrackingFunction,
    );
  };

  const onExportClick = () => {
    handleExportTracking();
    openExportModal();
  };

  const isMultipleCharts = [ExploreTabView.Leaderboard, ExploreTabView.Trends].includes(exploreTabView);

  return (
    <span className="flex items-center">
      <ExploreCollectionButton
        isDisabled={isExportButtonDisabled || isStackChartExportButtonDisabled} //NOSONAR typescript:S6606
        createInsightDataFn={createInsightDataFn}
        exploreViewForCollections={exploreChartView}
        createViewStateFn={createViewStateFn}
      />

      {onChange && isMultipleCharts ? (
        <ToggleViewButtons
          {...{
            view: viewType,
            isExportDisabled: isExportButtonDisabled || isStackChartExportButtonDisabled, //NOSONAR typescript:S6606
            onExportClick,
            onChange,
          }}
        />
      ) : (
        <WithExportButton
          disabled={isExportButtonDisabled || isStackChartExportButtonDisabled} //NOSONAR typescript:S6606
          onClick={onExportClick}
        ></WithExportButton>
      )}

      <WithExportModal
        isShown={isExportModalOpen}
        exportFormats={exportModalFormats}
        message={exportModalMessage}
        title={exportModalHeaderText}
        download={onChartDownload}
        onClose={onCloseExportModal}
        onError={onExportError}
      />
    </span>
  );
};

export default ExploreHeaderIcon;
